if (!customElements.get('search-suggestions')) {
  const SearchSuggestions = class extends HTMLElement {
    connectedCallback() {
      this.searchTimeoutID = -1;
      this.form = this.closest('form');
      this.container = this.closest('.cstm-search') || this.form;
      this.searchInput = this.form.querySelector('input[name="q"]');
      // Server-rendered popular items (shown before the shopper types anything).
      this.initialHTML = this.innerHTML;

      this.searchInput.addEventListener('keyup', this.handleInputChange.bind(this));
      this.searchInput.addEventListener('change', this.handleInputChange.bind(this));

      // Open on focus (reveals popular items immediately), close on outside click / Escape.
      this.searchInput.addEventListener('focus', () => this.open());
      this.searchInput.addEventListener('keyup', (evt) => {
        if (evt.key === 'Escape') this.close();
      });
      document.addEventListener('click', (evt) => {
        if (!this.container.contains(evt.target)) this.close();
      });
    }

    // Turn on internal scrolling ONLY when the results overflow the capped
    // panel. When they fit (e.g. just a few popular items), the panel stays
    // overflow:hidden so a touch-drag over it scrolls the PAGE instead of a
    // dead scroll area — and the page-scroll below dismisses it. Must run while
    // the panel is visible (display:none reports 0 height), hence rAF.
    updateScrollable() {
      this.classList.toggle(
        'is-scrollable',
        this.scrollHeight > this.clientHeight + 1
      );
    }

    scheduleScrollableUpdate() {
      requestAnimationFrame(() => this.updateScrollable());
    }

    open() {
      this.container.classList.add('is-open');
      // Lift the announcement bar above the sticky header's stacking context
      // so the dropdown is never painted under the logo/nav.
      document.body.classList.add('cstm-search-open');

      // Content is now visible — decide whether it needs internal scrolling.
      this.scheduleScrollableUpdate();
      if (!this.boundScrollableUpdate) {
        this.boundScrollableUpdate = this.scheduleScrollableUpdate.bind(this);
        // Orientation / viewport changes flip the scrollable state (max-height
        // is vh-based), so recompute while the panel is open.
        window.addEventListener('resize', this.boundScrollableUpdate, { passive: true });
      }

      // Close as soon as the shopper scrolls the PAGE, so the panel never
      // becomes a scroll-trap on mobile (it used to cover the screen with no
      // way to scroll the page past it). Scrolling INSIDE the panel does not
      // reach here — the panel has overscroll-behavior:contain. A small
      // threshold ignores the focus-jitter mobile browsers fire on open.
      if (!this.boundScrollClose) {
        this.openScrollY = window.scrollY;
        this.boundScrollClose = () => {
          if (Math.abs(window.scrollY - (this.openScrollY || 0)) > 40) {
            this.close();
          }
        };
        window.addEventListener('scroll', this.boundScrollClose, { passive: true });
      }
    }

    close() {
      this.container.classList.remove('is-open');
      document.body.classList.remove('cstm-search-open');
      // Drop focus too — the panel is also shown via :focus-within, so leaving
      // the input focused would keep it open.
      if (this.searchInput) this.searchInput.blur();
      if (this.boundScrollClose) {
        window.removeEventListener('scroll', this.boundScrollClose);
        this.boundScrollClose = null;
      }
      if (this.boundScrollableUpdate) {
        window.removeEventListener('resize', this.boundScrollableUpdate);
        this.boundScrollableUpdate = null;
      }
    }

    handleInputChange() {
      const valueToSearch = this.searchInput.value;

      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        this.open();
        clearTimeout(this.searchTimeoutID);

        this.searchTimeoutID = setTimeout(() => {
          this.fetchSuggestions(valueToSearch);
        }, 300);
      } else if (!valueToSearch.length) {
        clearTimeout(this.searchTimeoutID);

        if (this.searchSuggestionsAbortController) {
          this.searchSuggestionsAbortController.abort('Existing request not needed');
          this.searchSuggestionsAbortController = null;
        }

        // Restore the popular items rather than blanking the panel.
        this.innerHTML = this.initialHTML;
        this.scheduleScrollableUpdate();
      }

      this.oldSearchValue = valueToSearch;
    }

    fetchSuggestions(terms) {
      if (this.searchSuggestionsAbortController) {
        this.searchSuggestionsAbortController.abort('Existing request not needed');
      }
      this.searchSuggestionsAbortController = new AbortController();

      const url =
        `${theme.routes.predictiveSearch}?q=${encodeURIComponent(terms)}` +
        '&section_id=search-suggestions' +
        '&resources[type]=product,query,collection' +
        '&resources[limit]=10' + // API hard-caps at 10 per resource; show them all (no "view all")
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: this.searchSuggestionsAbortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const results = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.search-suggestions-wrapper');

          this.innerHTML = '';
          if (results && results.firstElementChild) {
            this.insertAdjacentElement('beforeend', results.firstElementChild);
          }
          // Results changed height — re-evaluate whether to scroll internally.
          this.scheduleScrollableUpdate();
        })
        .catch((error) => {
          if (error && error.code === 20) {
            return; // aborted
          }
          console.warn(error);
        });
    }
  };

  window.customElements.define('search-suggestions', SearchSuggestions);
}
